import { AnalyticsPixelIds } from 'core/analytics/entities';
import {
  AmplitudeClientService,
  AnalyticsClientService,
  AmplitudeServiceBuilder,
  FunnelStepClientService,
  AppnebulaHttpAxonService,
  AppnebulaHttpBingAdsService,
  AppnebulaHttpFacebookService,
  AppnebulaHttpSnapchatService,
  AppnebulaHttpTaboolaService,
  AppnebulaHttpTikTokService,
  ArrivalClientService,
  AxonServiceBuilder,
  BingAdsServiceBuilder,
  ConversionsAPIService,
  FacebookServiceBuilder,
  HttpAxonService,
  HttpBackendAnalyticsService,
  HttpBingAdsService,
  HttpFacebookService,
  HttpGTMService,
  HttpLtvPredictService,
  HttpSnapchatService,
  HttpSnitcherService,
  HttpTaboolaService,
  HttpTikTokService,
  LtvPredictServiceWithObservability,
  SnapchatServiceBuilder,
  SnitcherServiceBuilder,
  TaboolaServiceBuilder,
  TikTokServiceBuilder,
} from 'core/analytics/services';
import { AuthService } from 'core/auth/services';
import { BrowserAppResource } from 'core/common/observability/entities';
import { BrowserObservabilitySystemBuilder } from 'core/common/observability/services';
import { CookiesClientService } from 'core/common/services/cookie';
import { Logger } from 'core/common/services/logger/interfaces';
import { LocalStorage, SessionStorage } from 'core/common/services/storage';
import { ContentService } from 'core/content/services';
import { OneTrustCookieConsentService } from 'core/cookie-consent/services';
import { featureFlagsConfig } from 'core/feature-flags/config';
import { FeatureFlagsConfig } from 'core/feature-flags/entities';
import {
  CookiesIdFeatureFlagsDecorator,
  GrowthBookFeatureFlags,
  QuerySettingFeatureFlagsDecorator,
} from 'core/feature-flags/services';
import { FunnelServiceBuilder } from 'core/funnel/builders';
import { HandlebarsTemplateEngine } from 'core/funnel/services';
import { HumanDesignReportService } from 'core/human-design/services';
import { LocalizationService } from 'core/localization/services';
import { NatalChartService } from 'core/natal-chart/services';
import { OffersService } from 'core/offers/services';
import { PaymentService } from 'core/payments/services';
import { FaceDetectionService, PalmDetectionService } from 'core/scan/services';
import { SupportFormService } from 'core/support/services';
import {
  AutocompleteService,
  LocationClientService,
  SubscriptionsService,
  UserReadingService,
  UserService,
  UtmTagsClientService,
} from 'core/user/services';
import { ZodiacService } from 'core/zodiac/services';
import {
  AppClientEnvironment,
  ChannelAttributionClientService,
  ClarityHeatmapRecordingClientService,
  ContentManagerClientService,
  EmailManagerService,
  HttpClientBuilder,
  LoggerBuilder,
  MetaClientService,
} from '../services';
import { ErrorMonitoringClientService } from '../services/errorMonitoring';
import { FunnelSettingsService } from '../services/funnel-settings';
import {
  GrafanaLoggerBatchDestination,
  GrafanaLoggerService,
} from '../services/grafana-logger/services';
import {
  BaseLogsBatchCache,
  LogsBatchCacheWithLocalStorage,
} from '../services/logger/batch-logger-transport';
import { StorePersistenceClientService } from '../store/services';
import { Config } from './ConfigContext';
import type { Services } from './ServicesContext';

export const initClientDependencies = (
  config: Config,
  options: {
    analyticsPixelIds?: AnalyticsPixelIds;
    features?: FeatureFlagsConfig['features'];
  },
): Services => {
  const { analyticsPixelIds, features } = options;

  const cookiesService = new CookiesClientService({
    domain: config.rootDomain,
  });

  const appEnvironment = new AppClientEnvironment(cookiesService, config);

  const resource = BrowserAppResource.create();
  resource.setAppName(config.appName).setEnv(config.appEnvironment);

  const observabilitySystem = BrowserObservabilitySystemBuilder.create({
    defaultTracerName: config.observability.defaultTracerName,
  })
    .addBrowserTraceProvider({ resource })
    .withSplitId(() => appEnvironment.getSplitId())
    .withHttpSpanExporter({
      url: config.observability.tracesUrl,
      headers: {},
    })
    .getResult();

  const grafanaHttpClient = new HttpClientBuilder(config.grafanaLogger.url)
    .addObservability(observabilitySystem)
    .addRequestId()
    .getResult();

  let loggingService: Logger;

  if (config.grafanaLogger.enabled) {
    loggingService = new LoggerBuilder()
      .addConsoleTransport()
      .addBatchTransport(
        new GrafanaLoggerBatchDestination(new GrafanaLoggerService(grafanaHttpClient)),
        new LogsBatchCacheWithLocalStorage(new BaseLogsBatchCache()),
      )
      .addContext({ context: config.appName })
      .getResult();
  } else {
    loggingService = new LoggerBuilder().addContext({ context: config.appName }).getResult();
  }

  const sessionStorage = new SessionStorage(loggingService);
  const localStorage = new LocalStorage(loggingService);

  const httpClient = new HttpClientBuilder(config.apiUrl)
    .addObservability(observabilitySystem)
    .addRequestId()
    .addPersistentHeaders(localStorage)
    .addPersistentHeaders(sessionStorage, localStorage)
    .getResult();
  const publicHttpClient = new HttpClientBuilder(config.apiUrl, {
    withCredentials: false,
    headers: {},
  }).getResult();

  const errorMonitoringService = new ErrorMonitoringClientService();
  const metaService = new MetaClientService(sessionStorage);
  const utmTagsService = UtmTagsClientService.createFromQueryParams(
    cookiesService,
    loggingService,
    sessionStorage,
  );

  const cookieConsentService = new OneTrustCookieConsentService(cookiesService, loggingService);
  const conversionsAPIService = new ConversionsAPIService(cookiesService);
  const funnelSettingsService = new FunnelSettingsService(config);

  const coreFacebookService = new FacebookServiceBuilder(
    new HttpFacebookService(cookieConsentService, analyticsPixelIds?.facebook.nonConditional),
  )
    .addMonitoring(observabilitySystem, errorMonitoringService, funnelSettingsService)
    .getResult();
  const facebookService = new AppnebulaHttpFacebookService(coreFacebookService);
  const gtmService = new HttpGTMService(observabilitySystem, funnelSettingsService);
  const ltvPredictService = new LtvPredictServiceWithObservability(
    new HttpLtvPredictService(httpClient, loggingService, config),
    observabilitySystem,
    funnelSettingsService,
  );
  const funnelStepService = new FunnelStepClientService(funnelSettingsService, sessionStorage);
  const amplitudeService = new AmplitudeServiceBuilder(
    new AmplitudeClientService(
      observabilitySystem,
      cookiesService,
      funnelSettingsService,
      config.analytics.amplitudeApiKey,
      config.analytics.amplitudeApiEndpoint,
    ),
  )
    .addFunnelStep(funnelStepService)
    .addFunnelContext(funnelSettingsService)
    .addMonoLanding(false)
    .getResult();
  const backendAnalyticsService = new HttpBackendAnalyticsService(
    httpClient,
    loggingService,
    appEnvironment,
    sessionStorage,
  );

  const growthBookInitConfig = {
    ...featureFlagsConfig,
    features: { ...(featureFlagsConfig.features ?? {}), ...(features ?? {}) },
  };

  const growthBookFeatureFlags = new GrowthBookFeatureFlags(
    growthBookInitConfig,
    appEnvironment,
    config,
    loggingService,
  );

  const featureFlagsWithIdentify = new CookiesIdFeatureFlagsDecorator(
    growthBookFeatureFlags,
    cookiesService,
    config,
    loggingService,
  );
  const featureFlags = new QuerySettingFeatureFlagsDecorator(featureFlagsWithIdentify);
  const snitcherService = new SnitcherServiceBuilder(
    new HttpSnitcherService(httpClient, loggingService, featureFlags),
  )
    .addFunnelContext(funnelSettingsService, funnelStepService)
    .getResult();
  const coreTikTokService = new TikTokServiceBuilder(
    new HttpTikTokService(
      cookiesService,
      cookieConsentService,
      analyticsPixelIds?.tikTok.nonConditional,
    ),
  )
    .addMonitoring(observabilitySystem, errorMonitoringService, funnelSettingsService)
    .getResult();
  const coreBingAdsService = new BingAdsServiceBuilder(
    new HttpBingAdsService(cookieConsentService, analyticsPixelIds?.bingAds.nonConditional),
  )
    .addMonitoring(observabilitySystem, errorMonitoringService, funnelSettingsService)
    .getResult();
  const coreTaboolaService = new TaboolaServiceBuilder(
    new HttpTaboolaService(cookieConsentService, analyticsPixelIds?.taboola.nonConditional),
  )
    .addMonitoring(observabilitySystem, errorMonitoringService, funnelSettingsService)
    .getResult();
  const tikTokService = new AppnebulaHttpTikTokService(coreTikTokService);
  const taboolaService = new AppnebulaHttpTaboolaService(coreTaboolaService);
  const coreAxonService = new AxonServiceBuilder(
    new HttpAxonService(cookieConsentService, analyticsPixelIds?.axon.nonConditional),
  )
    .addMonitoring(observabilitySystem, errorMonitoringService, funnelSettingsService)
    .getResult();
  const axonService = new AppnebulaHttpAxonService(coreAxonService);
  const coreSnapchatService = new SnapchatServiceBuilder(
    new HttpSnapchatService(
      cookieConsentService,
      localStorage,
      analyticsPixelIds?.snapchat.nonConditional,
    ),
  )
    .addMonitoring(observabilitySystem, errorMonitoringService, funnelSettingsService)
    .getResult();
  const snapchatService = new AppnebulaHttpSnapchatService(coreSnapchatService);

  const bingAdsService = new AppnebulaHttpBingAdsService(coreBingAdsService);

  const analyticsService = new AnalyticsClientService(
    amplitudeService,
    conversionsAPIService,
    tikTokService,
    facebookService,
    gtmService,
    utmTagsService,
    ltvPredictService,
    backendAnalyticsService,
    loggingService,
    bingAdsService,
    taboolaService,
    cookiesService,
    funnelSettingsService,
    snapchatService,
    snitcherService,
  );

  const arrivalService = new ArrivalClientService(
    utmTagsService,
    httpClient,
    localStorage,
    loggingService,
    { splitId: featureFlags.getSplitId(), isTester: appEnvironment.isTestingEnv() },
  );

  const zodiacService = new ZodiacService();
  const locationService = new LocationClientService(httpClient, cookiesService);
  const userService = new UserService(httpClient);
  const userReadingService = new UserReadingService(httpClient);
  const natalChartService = new NatalChartService(httpClient);
  const contentService = new ContentService(httpClient);
  const authService = new AuthService(httpClient);
  const autocompleteService = new AutocompleteService(config.geocodeUrl, config.geocodeApiKey);
  const palmDetectionService = new PalmDetectionService(httpClient);
  const faceDetectionService = new FaceDetectionService(httpClient);
  const offersService = new OffersService(httpClient, locationService, funnelSettingsService);
  const localizationService = new LocalizationService();
  const templateEngine = new HandlebarsTemplateEngine();
  const funnelService = FunnelServiceBuilder.create(config);
  const humanDesignReportService = new HumanDesignReportService(httpClient);
  const storePersistenceService = new StorePersistenceClientService(loggingService);
  const channelAttributionService = new ChannelAttributionClientService();
  const heatmapRecordingService = new ClarityHeatmapRecordingClientService(
    loggingService,
    analyticsService,
    cookieConsentService,
    errorMonitoringService,
  );
  const emailManagerService = new EmailManagerService(httpClient);
  const subscriptionService = new SubscriptionsService(httpClient);
  const contentManagerService = new ContentManagerClientService(publicHttpClient);
  const paymentService = new PaymentService(
    httpClient,
    loggingService,
    funnelSettingsService,
    locationService,
    featureFlags,
    config,
  );
  const supportFormService = new SupportFormService(httpClient);

  return {
    featureFlags,
    appEnvironment,
    loggingService,
    utmTagsService,
    zodiacService,
    cookiesService,
    analyticsService,
    locationService,
    authService,
    userService,
    userReadingService,
    natalChartService,
    contentService,
    autocompleteService,
    palmDetectionService,
    faceDetectionService,
    offersService,
    templateEngine,
    cookieConsentService,
    funnelService,
    humanDesignReportService,
    observabilitySystem,
    heatmapRecordingService,
    storePersistenceService,
    errorMonitoringService,
    metaService,
    channelAttributionService,
    tikTokService,
    taboolaService,
    axonService,
    snapchatService,
    gtmService,
    funnelSettingsService,
    funnelStepService,
    facebookService,
    localizationService,
    httpClient,
    snitcherService,
    arrivalService,
    emailManagerService,
    subscriptionService,
    contentManagerService,
    paymentService,
    supportFormService,
  };
};
